// client/src/wordpress_api.ts

import { Map } from 'immutable'
import axios, { AxiosResponse } from 'axios'
import { Acf, Gutenberg } from './types_and_parser/types.ext.gen'
import { Link } from '../cms/blocks/types.gen'
import { AsyncState } from 'widgets-for-react'
import { PaginationResponse } from './types_and_parser/extra_types.mod'

export interface Headers {
  'X-WP-Nonce'?: string
}

export namespace Store {
  export let store: Map<string, any> = Map<string, any>()

  export function read<T>(key: string): T {
    return store.get(key)
  }

  export function write<T>(key: string, data: T) {
    store = store.set(key, data)
    return data
  }
}


export namespace WordPress {
  export type PostType = 'post' | 'page' | 'update' | 'faq' | 'form' | 'news' | 'story'
  export type LocationType = 'copyright' | 'directory' | 'footer' | 'main' | 'recommended'
  export type PostId = number
  export type AuthorId = number
  export type CategoryId = number
  export type TagId = number
  export type AttachmentId = number
  export type DateTimeString = string // '2018-03-04T14:51:59'
  export type Url = string
  export type CommentStatus = 'open' | 'closed'
  export type PingStatus = 'open' | 'closed'
  export type TermId = number

  export interface PostObject {
    ID: PostId
    post_author: AuthorId
    post_date: DateTimeString
    post_date_gmt: DateTimeString
    post_content: string
    post_title: string
    post_excerpt: string
    post_status:
      | 'publish'
      | 'pending'
      | 'draft'
      | 'auto-draft'
      | 'future'
      | 'private'
      | 'inherit'
      | 'trash'
    comment_status: CommentStatus
    ping_status: PingStatus
    post_password: string
    post_name: string
    to_ping: string
    pinged: string
    post_modified: DateTimeString
    post_modified_gmt: DateTimeString
    post_content_filtered: string
    post_parent: PostId
    guid: string
    menu_order: number
    post_type: string
    post_mime_type: string
    comment_count: number
  }

  export interface PostRequestArguments {
    after?: DateTimeString // ISO8601
    author?: number
    author_exclude?: number
    before?: DateTimeString // ISO8601
    categories?: number
    categories_exclude?: number
    exclude?: number
    include?: string // commas separated list 1,2,3
    order?: 'asc' | 'desc'
    orderby?:
      | 'author'
      | 'date'
      | 'id'
      | 'include'
      | 'modified'
      | 'parent'
      | 'relevance'
      | 'slug'
      | 'include_slugs'
      | 'title'
      | 'menu_order'
      | 'random'
    page?: number
    per_page?: number
    search?: string
    slug?: string
    status?:
      | 'publish'
      | 'pending'
      | 'draft'
      | 'auto-draft'
      | 'future'
      | 'private'
      | 'inherit'
      | 'trash'
    sticky?: number
    tags?: number
    tags_exclude?: number
    _embed?: boolean
    themes?: number
    articles?: number
    _wpnonce?: string
    pagename?: string
    post_type?: string // comma seperated post type names
  }

  export type BreadcrumbElement = {
    position: number
    item: {
      '@id': string
      name: string
    }
  }

  export type RedirectResponse = {
    type: 'redirect'
    to: string
  }

  export type PostResponse<PostType> = {
    id: PostId
    type: PostType
    date: DateTimeString
    date_gmt: DateTimeString
    guid: {
      rendered: string
    }
    modified: DateTimeString
    modified_gmt: DateTimeString
    slug: string
    status:
      | 'publish'
      | 'pending'
      | 'draft'
      | 'auto-draft'
      | 'future'
      | 'private'
      | 'inherit'
      | 'trash'
    link: Url
    title: {
      rendered: string
    }
    content: {
      rendered: string
      protected: boolean
    }
    excerpt: PostResponse<PostType>['content']
    author: AuthorId
    parent: false | PostObject
    is_front: boolean
    featured_media: number
    featured_media_object: MediaResponse
    comment_status: CommentStatus
    ping_status: PingStatus
    sticky: boolean
    format:
      | 'standard'
      | 'aside'
      | 'chat'
      | 'gallery'
      | 'link'
      | 'image'
      | 'quote'
      | 'status'
      | 'video'
      | 'audio'
    //meta: []
    yoast?: YoastResponse
    categories: Array<CategoryId>
    tags: Array<TagId>
    acf: {
      buildingblocks?: Array<Acf.Block>
    }
    blocks_custom: Array<Gutenberg.Block>
    breadcrumb: {
      '@context': string
      '@type': 'BreadcrumbList'
      itemListElement: Array<BreadcrumbElement>
    }
  }

  export interface PageResponse extends WordPress.PostResponse<'page'> {
    acf: {
      buildingblocks?: Array<Acf.Block>
      title: string
      intro_text?: string
      sub_text?: string
      media_type?: 'video' | 'image' | 'nothing'
      image?: Acf.ImageArray
      video_type?: 'youtube' | 'vimeo' | 'video' | 'audio'
      video_id?: string
      video_placeholder?: Acf.ImageArray
      background_image?: Acf.ImageArray
      background_image_mobile?: Acf.ImageArray
      links: Array<{
        link_title: Acf.Text
        link_url: Acf.Text
      }>
    }
    template:
      | ''
      | 'news-overview'
      | 'story-overview'
      | 'contact'
  }

  export interface NewsArticleResponse extends WordPress.PostResponse<'news'> {
    news_taxonomy: Array<TaxonomyTerm>
    acf: {
      buildingblocks?: Array<Acf.Block>
      title: string
      intro_text?: string
      sub_text?: string
      media_type?: 'video' | 'image' | 'nothing'
      image?: Acf.ImageArray
      video_type?: 'youtube' | 'vimeo' | 'video' | 'audio'
      video_id?: string
      video_placeholder?: Acf.ImageArray
      background_image?: Acf.ImageArray
      background_image_mobile?: Acf.ImageArray
      links: Array<{
        link_title: Acf.Text
        link_url: Acf.Text
      }>
      teaser_image: Acf.ImageArray
    }
  }

  export interface StoryResponse extends WordPress.PostResponse<'story'> {
    story_taxonomy: Array<TaxonomyTerm>
    acf: {
      buildingblocks?: Array<Acf.Block>
      title: string
      intro_text?: string
      sub_text?: string
      media_type?: 'video' | 'image' | 'nothing'
      image?: Acf.ImageArray
      video_type?: 'youtube' | 'vimeo' | 'video' | 'audio'
      video_id?: string
      video_placeholder?: Acf.ImageArray
      background_image?: Acf.ImageArray
      background_image_mobile?: Acf.ImageArray
      links: Array<{
        link_title: Acf.Text
        link_url: Acf.Text
      }>
      teaser_image: Acf.ImageArray
    }
  }

  export type OverviewResponse = {
    total_pages: number,
    total_articles: number
  } & ({
    kind: 'news',
    posts: Array<NewsArticleResponse>
  } | {
    kind: 'story',
    posts: Array<StoryResponse>
  })

  export type Overview<PostType> = {
    current_page: number
    async: AsyncState<OverviewResponse>
    kind: PostType
  } & ({
    kind: 'news'
    categories?: AsyncState<Array<TaxonomyResponse>>
    years?: AsyncState<Array<number>>
  } | {
    kind: 'story'
    categories?: AsyncState<Array<TaxonomyResponse>>
  })

  export type OptionsResponse = {
    acf: {
      global_about: string
      about_cta?: Link
      global_address: string
      global_contact: string
      facebook_url?: string
      linkedin_url?: string
      instagram_url?: string
      twitter_url?: string
    }
  }

  export type PostTypeResponse =
    | PageResponse
    | NewsArticleResponse
    | StoryResponse

  export type ArticleResponse =
    | NewsArticleResponse
    | StoryResponse

  export type MilkPrice = {
    year: string
    year_description_en: string
    year_description_nl: string
    prices_per_month: Array<
      {
        month: string
        milk_price: string
        protein_price: string
        fat_price: string
        lactose_price: string
        average_price: string
      }
    >
  }


  export type MilkPricesResponse =
    {
      acf: {
        bio_prices: Array<MilkPrice>
        bio_prices_title:  string
        bio_prices_subtitle: string
        prices: Array<MilkPrice>
        prices_subtitle: string
        prices_title: string
      }
    }

  export type LocationResponse = PostResponse<'location'> & {
    acf: {
      street: string
      number?: string
      number_addition?: string
      postal_code?: string
      city: string
      country: string
      text?: string
      latitude: string
      longitude: string,
      offices?: Array<{
        function: string
        street: string
        number?: string
        number_addition?: string
        postal_code?: string
        city: string
        country: string
        phone_number: string
      }>
    }
  }

  export interface TaxonomyResponse {
    id: number
    term_id: number
    count: number
    description: string
    link: string
    name: string
    slug: string
    taxonomy: string
    parent: number
    meta: {}
    acf?: {}
    term_order: string
  }

  export type TaxonomyTerm = {
    count: number
    description: string
    filter: string
    name: string
    parent: number
    slug: string
    taxonomy: string
    term_group: number
    term_id: number
    term_taxonomy_id: number
  }

  export class Image {
    id: AttachmentId
    date: DateTimeString
    date_gmt: DateTimeString
    guid: {
      rendered: Url
    }
    modified: DateTimeString
    modified_gmt: DateTimeString
    slug: string
    status: 'inherit'
    type: 'attachment'
    link: Url
    title: {
      rendered: string
    }
    author: AuthorId
    comment_status: CommentStatus
    ping_status: PingStatus
    template: ''
    meta: Array<any> 
    acf?: Array<any> 
    description: {
      rendered: string
    }
    caption: {
      rendered: string
    }
    alt_text: string
    media_type: 'image' 
    mime_type: 'image/jpeg' | 'image/png' 
    media_details: {
      width: number
      height: number
      file: string
      sizes: ImageSizes
      image_meta: {
        aperture: string
        credit: string
        camera: string
        caption: string
        created_timestamp: string
        copyright: string
        focal_length: string
        iso: string
        shutter_speed: string
        title: string
        orientation: string
        keywords: Array<string>
      }
    }
    post: PostId
    source_url: Url
    _links?: {
      self: [
        {
          href: Url
        }
      ]
      collection: [
        {
          href: Url
        }
      ]
      about: [
        {
          href: Url
        }
      ]
      author: [
        {
          embeddable: boolean
          href: Url
        }
      ]
      replies: [
        {
          embeddable: boolean
          href: Url
        }
      ]
    }
  }

  export interface ImageSize {
    file: string
    width: number
    height: number
    mime_type: string
    source_url: string
  }

  export type ImageSizes = {
    full?: ImageSize
    large?: ImageSize
    medium_large?: ImageSize
    medium?: ImageSize
    thumbnail?: ImageSize
    media?: ImageSize
    teaser?: ImageSize
    grid?: ImageSize
    quote?: ImageSize
    attention?: ImageSize
    stack?: ImageSize
    square?: ImageSize
    header?: ImageSize
  }

  export type MediaResponse = Image

  export type MenuItem = {
    ID: MenuId
    order: number
    parent: number
    title: string
    url: Url
    attr: string
    target: string
    classes: string
    xfn: string
    description: string
    object_id: number
    object: string
    type: string
    type_label: string
    child_items: Array<MenuItem>
    button_label?: string
  }

  export type MenuId = number
  export type MenuLocationResponse = Array<MenuItem>
  export type MenuLocationsResponse = {
    [key: string]: {
      ID: MenuId
      label: string
      meta: {
        links: {
          collection: Url
          // self: Url // self gives back wrong namespace, don't use this
        }
      }
    }
  }

  export type YoastResponse = {
    focuskw?: string
    title?: string
    metadesc?: string
    linkdex?: string
    metakeywords?: string
    meta_robots_noindex?: string
    meta_robots_nofollow?: string
    meta_robots_adv?: string
    canonical?: string
    redirect?: string
    opengraph_title?: string
    opengraph_description?: string
    opengraph_image?: string
    twitter_title?: string
    twitter_description?: string
    twitter_image?: string
    breadcrumb?: string
  }

  function RestApislugForPostType(post_type: PostType): string {
    switch (post_type) {
      case 'post':
        return 'posts'
      case 'page':
        return 'pages'
      case 'update':
        return 'updates'
      case 'faq':
       return 'faqs'
      case "form":
        return "forms"
      case "news":
        return "news"
      case 'story':
        return 'stories'
    }
  }

  export function api<R>() {
    return new Api<R>()
  }

  export class Api<R> {
    args: PostRequestArguments = {}
    endpoint: string
    post_type: PostType

    constructor() {
      return this
    }
    select(post_type: PostType) {
      this.post_type = post_type
      this.endpoint = `${window.wp.api}/wp/v2/${RestApislugForPostType(post_type)}`
      return this
    }

    where(args: PostRequestArguments) {
      this.args = Object.entries(args).reduce((xs, x) => x[1] == "" || x[1] == undefined ? xs : ({...xs, [x[0]]: x[1]}), {})
      return this
    }

    getOne(): Promise<AxiosResponse<R>> {
      const storeKey = `${window.wp.api}/wp/v2/${this.post_type}${JSON.stringify(this.args)}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params: { [key: string]: string } = {}
      if (window.wp.is_user_logged_in) {
        params['_wpnonce'] = window.wp.nonce
      }

      return axios
        .get<Array<R>>(this.endpoint, { params: this.args }) //
        .then(function(response): Promise<AxiosResponse<R>> {
          if (response && response.data && response.data[0]) {
            let result: AxiosResponse<R> = {
              ...response,
              data: response.data[0]
            }

            Store.write(storeKey, result)
            return Promise.resolve(result)
          } else {
            return Promise.reject('No Data')
          }
        })
        .catch((error: string) => {
          return Promise.reject(error)
        })
    }

    getMultiple(): Promise<AxiosResponse<Array<R>>> {
      const storeKey = `${window.wp.api}/wp/v2/${this.post_type}${JSON.stringify(this.args)}`
      const val = Store.read<AxiosResponse<Array<R>>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params: { [key: string]: string } = {}
      if (window.wp.is_user_logged_in) {
        params['_wpnonce'] = window.wp.nonce
      }

      return axios
        .get<Array<R>>(this.endpoint, { params: this.args }) //
        .then(function(response) {
          if (response) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    findById(id: string | number): Promise<AxiosResponse<R>> {
      const storeKey = `${this.endpoint}/wp/v2/${this.post_type}/${id}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params = this.args
      if (window.wp.is_user_logged_in) {
        params['_wpnonce'] = window.wp.nonce
      }

      return axios
        .get<R>(`${this.endpoint}/${id}`, { params: params }) //
        .then(function(response) {
          if (response && response.data) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    frontpage(): Promise<AxiosResponse<R>> {
      const request_url = `/wp-json/wp/v2/frontpage`
      const val = Store.read<AxiosResponse<R>>(request_url)
      if (val) {
        return Promise.resolve(val)
      }
      return axios
        .get<R>(request_url) //
        .then(function(response) {
          if (response && response.data) {
            Store.write(request_url, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    current(path: string): Promise<AxiosResponse<PostTypeResponse | RedirectResponse>> {
      const request_url = `/wp/v2/current`
      const params = {
        url: path
      }
      const store_key = `${window.wp.api}${request_url}${JSON.stringify(params)}`
      const val = Store.read<AxiosResponse<PostTypeResponse>>(store_key)

      if (val) return Promise.resolve(val)

      return axios
        .get<PostTypeResponse>(`${window.wp.api}${request_url}`, { params }) //
        .then(function(response) {
          if (response && response.data) {
            Store.write(store_key, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    anypost(id: number): Promise<AxiosResponse<PostTypeResponse>> {
      const request_url = `/wp-json/wp/v2/anypost/${id}`
      const val = Store.read<AxiosResponse<PostTypeResponse>>(request_url)

      if (val) {
        return Promise.resolve(val)
      }

      return axios
        .get<PostTypeResponse>(request_url) //
        .then(function(response) {
          if (response && response.data) {
            Store.write(request_url, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    options(id: string): Promise<AxiosResponse<OptionsResponse>> {
      const request_url = `/wp-json/acf/v3/options/${id}`
      const val = Store.read<AxiosResponse<OptionsResponse>>(request_url)

      if (val) {
        return Promise.resolve(val)
      }

      return axios
        .get<OptionsResponse>(request_url) //
        .then(function(response) {
          if (response && response.data) {
            Store.write(request_url, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    search(term: string, page?: number): Promise<AxiosResponse<PaginationResponse<PostTypeResponse[]>>> {
      const request_url = `${window.wp.api}/relevanssi/v1/search/?s=${term}${page ? `&page=${page}` : ''}`
      const stored = Store.read<AxiosResponse<PaginationResponse<PostTypeResponse[]>>>(request_url)

      if (stored) {
        return Promise.resolve(stored)
      }

      return axios.get<PaginationResponse<PostTypeResponse[]>>(request_url).then(response => {
        if (response && response.status) {
          Store.write(request_url, response)
          return Promise.resolve(response)
        }
        return Promise.reject('Failure')
      })
    }

    years(post_type: PostType): Promise<AxiosResponse<Array<R>>> {
      const storeKey = `${window.wp.api}/vidda/v1/years?post_type=${post_type}`
      const val = Store.read<AxiosResponse<Array<R>>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }
  
      let params: { [key: string]: string } = {}
      if (window.wp.is_user_logged_in) {
        params['_wpnonce'] = window.wp.nonce
      }
  

      return axios
        .get<Array<R>>(storeKey) //
        .then(function(response) {
          if (response) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    terms(taxonomy: string): Promise<AxiosResponse<Array<R>>> {
      const storeKey = `${window.wp.api}/wp/v2/${taxonomy}`
      const val = Store.read<AxiosResponse<Array<R>>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }
  
      let params: { [key: string]: string } = {}
      if (window.wp.is_user_logged_in) {
        params['_wpnonce'] = window.wp.nonce
      }
  

      return axios
        .get<Array<R>>(storeKey) //
        .then(function(response) {
          if (response) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }  
  }


  export function media<R>() {
    return new MediaApi<R>()
  }

  export class MediaApi<R> {
    endpoint: string
    id: number

    constructor() {
      return this
    }

    getOne(): Promise<AxiosResponse<R>> {
      const storeKey = `${this.endpoint}/wp-json/wp/v2/media/${this.id}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params: { [key: string]: string } = {}
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<Array<R>>(this.endpoint) //
        .then(function(response): Promise<AxiosResponse<R>> {
          if (response && response.data && response.data[0]) {
            let result: AxiosResponse<R> = {
              ...response,
              data: response.data[0]
            }

            Store.write(storeKey, result)
            return Promise.resolve(result)
          } else {
            return Promise.reject('No Data')
          }
        })
        .catch(error => Promise.reject(error))
    }

    findById(id: string | number): Promise<AxiosResponse<R>> {
      this.endpoint = `${window.wp.api}/wp/v2/media`

      const storeKey = `${this.endpoint}/wp/v2/media/${id}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params = {}
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<R>(`${this.endpoint}/${id}`, { params: params }) //
        .then(function(response) {
          if (response && response.data) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
        .catch(error => Promise.reject(error))
    }
  }

  

  export const findMenuByLocation = (location: LocationType):Promise<AxiosResponse<MenuLocationResponse>> => {
    const storeKey: string = location == 'copyright' ? `wp-api-menus/v2/menu-locations/${location}` : `menus/v1/locations/${location}`
    const val = Store.read<AxiosResponse<WordPress.MenuLocationResponse>>(storeKey)

    if (val) {
      return Promise.resolve(val)
    }
    return axios.get<any>(`${window.wp.api}/${storeKey}`).then(function(response) {
    if (response && response.data) {
        if(location != 'copyright') {
          response.data = response.data.items
        }
        else {
          response.data['child_items'] = response.data.children
        }
        Store.write(storeKey, response)
        return Promise.resolve(response)
      } else {
        return Promise.reject('Failure')
      }
    }).catch(error => Promise.reject(error))
  }
}


export type Option<T> = { kind: 'none' } | { kind: 'some'; value: T }
export type withDataState<T> =
  | {
    kind: 'loading'
  }
  | {
    kind: 'error'
    errors: {
      [key: string]: string
    }
  }
  | {
    kind: 'loaded'
    data: Option<T>
    pagination: {
      next: {
        page: string
        per_page: string
        rel: 'next'
        url: string
      }
      prev: {
        page: string
        per_page: string
        rel: 'prev'
        url: string
      }
      last: {
        page: string
        per_page: string
        rel: 'last'
        url: string
      }
    }
  }
